import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { contains } from 'ramda';
import {
    fetchCurrentTenant,
    updateSummary,
    saveTenant,
} from '../../actions/reduxActions/currentTenant';
import DeveloperName from './DeveloperName';
import UserList from './UserList';
import SubtenantList from './SubtenantList';
import Security from './Security';
import Reporting from './Reporting';
import Restrictions from './Restrictions';
import translations from '../../translations';
import OtlpSettings from './otlp/OtlpSettings';

// eslint-disable-next-line import/no-named-default
import { default as SAMLSettingsComponent } from './saml/SAMLSettings';
import { useScrollspyHook } from '../generic/scrollspyHook';

import '../../../css/tenant.less';

export const IP_RANGE = {
    admin: 'authorizedAdminIPRanges',
    draw: 'authorizedDrawIPRanges',
    packaging: 'authorizedPackagingIPRanges',
    run: 'authorizedRunIPRanges',
};

export const Tenant = ({
    currentTenant,
    authenticationToken,
    navigateTo,
    setActiveSectionId,
    hasMenu,
    getCurrentTenant,
    amendSummary,
    storeTenant,
}) => {
    if (currentTenant === null) {
        return null;
    }

    const { securitySettings, developerName, developerSummary, id: tenantId, observabilitySettings } = currentTenant;
    const isSubtenant = contains('+', developerName);

    useEffect(() => {
        getCurrentTenant(authenticationToken, tenantId);
    }, []);

    const contentRef = useRef(null);

    // using a scrollspy based on the intersection observer to report back to
    // the side-menu the currently active section so the side-menu can update
    // itself (every content page with a side-menu will have to implement this
    // hook)
    useScrollspyHook(contentRef, hasMenu, setActiveSectionId);

    return (
        <>
            <div className="admin-page tenant-page" ref={contentRef}>

                <h2 id="tenant-name">Tenant Name</h2>

                <DeveloperName />

                <h2 id="tenant-users">Users</h2>
                <p>
                    {translations.TENANT_user_description}
                </p>
                <UserList authenticationToken={authenticationToken} navigateTo={navigateTo} tenantId={tenantId} />

                {
                    developerName === '' || isSubtenant
                        ? null
                        : (
                            <>
                                <h2 id="tenant-subtenants">Subtenants</h2>
                                <p>
                                    {translations.TENANT_subtenant_description}
                                </p>
                                <SubtenantList authenticationToken={authenticationToken} navigateTo={navigateTo} tenantId={tenantId} />
                            </>
                        )
                }

                <h2 id="tenant-single-sign-on">Single Sign-on</h2>
                <p>
                    {translations.TENANT_sso_description}
                </p>

                <SAMLSettingsComponent
                    isActive={securitySettings ? securitySettings.isSamlEnabled : false}
                    settings={securitySettings ? securitySettings.samlSettings : null}
                />

                <h2 id="tenant-security">Security</h2>
                <p>
                    {translations.TENANT_security_description}
                </p>

                <Security {...securitySettings} />

                <h2 id="tenant-reporting">Reporting</h2>
                <p>
                    {translations.TENANT_reporting_description}
                </p>

                <Reporting />

                <h2 id="tenant-regional">Regional Restrictions</h2>
                <p>
                    {translations.TENANT_restrictions_description}
                </p>

                <div className="flex">
                    <Restrictions />
                </div>

                <OtlpSettings settings={observabilitySettings} />

                <h2 id="tenant-comments">Comments</h2>
                <div className="form-group">
                    <label>Comments about this Tenant</label>
                    <textarea
                        maxLength="1000"
                        rows="3"
                        className="form-control tenant-comments"
                        value={developerSummary || ''}
                        onChange={amendSummary}
                    />
                </div>

            </div>
            <div className="mw-footer container-fluid">
                <button
                    type="button"
                    className="btn btn-primary outcome tenant-save"
                    onClick={() => storeTenant(authenticationToken, tenantId)}
                >
                    Save Settings
                </button>
            </div>
        </>
    );
};

const mapStateTopProps = state => ({
    currentTenant: state.currentTenant,
});

const mapDispatchToProps = {
    // renamed to avoid shadowing (and appease the linter)
    getCurrentTenant: fetchCurrentTenant,
    amendSummary: updateSummary,
    storeTenant: saveTenant,
};

export default connect(
    mapStateTopProps,
    mapDispatchToProps,
)(Tenant);
